import { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { useAuth } from '../context/AuthContext';
import { expensesAPI } from '../lib/api';
import { formatCurrency, formatDate, expenseCategoryLabels, getStartOfMonth, getEndOfMonth, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Plus, 
  DollarSign, 
  Trash2,
  Loader2,
  TrendingDown,
  Building,
  Zap,
  ShoppingBag,
  Users,
  Megaphone,
  Wrench
} from 'lucide-react';
import { toast } from 'sonner';

const categoryIcons = {
  rent: Building,
  utilities: Zap,
  supplies: ShoppingBag,
  payroll: Users,
  marketing: Megaphone,
  maintenance: Wrench,
  other: DollarSign
};

export default function GastosPage() {
  const { selectedBusiness } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [startDate, setStartDate] = useState(getStartOfMonth());
  const [endDate, setEndDate] = useState(getEndOfMonth());
  
  // Returns today's date in CDMX (America/Mexico_City) formatted as YYYY-MM-DD.
  // Using `toISOString()` returns UTC — which after 6pm CDMX is already the
  // next day, so the expense form would default to tomorrow's date.
  const todayCDMX = () =>
    new Date().toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' });

  const [formData, setFormData] = useState({
    category: 'other',
    description: '',
    amount: 0,
    date: todayCDMX(),
    notes: ''
  });

  const loadExpenses = useCallback(async () => {
    if (!selectedBusiness) return;
    
    try {
      setLoading(true);
      const response = await expensesAPI.getAll({ start_date: startDate, end_date: endDate });
      setExpenses(response.data);
    } catch (error) {
      logger.error('Error loading expenses:', error);
      toast.error('Error al cargar gastos');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness, startDate, endDate]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  const handleSubmit = async () => {
    if (!formData.description || formData.amount <= 0) {
      toast.error('Descripción y monto son requeridos');
      return;
    }

    try {
      await expensesAPI.create({
        ...formData,
        business_id: selectedBusiness.id
      });
      toast.success('Gasto registrado');
      setDialogOpen(false);
      loadExpenses();
      setFormData({
        category: 'other',
        description: '',
        amount: 0,
        date: todayCDMX(),
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (expense) => {
    if (!window.confirm('¿Estás seguro de eliminar este gasto?')) return;
    
    try {
      await expensesAPI.delete(expense.id);
      toast.success('Gasto eliminado');
      loadExpenses();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);
  const expensesByCategory = expenses.reduce((acc, e) => {
    acc[e.category] = (acc[e.category] || 0) + e.amount;
    return acc;
  }, {});

  return (
    <div className="space-y-6 animate-fade-in" data-testid="gastos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Gastos
          </h1>
          <p className="text-slate-500 mt-1">
            Control de gastos de {selectedBusiness?.name}
          </p>
        </div>
        <Button 
          onClick={() => setDialogOpen(true)}
          className="bg-primary text-white hover:bg-primary/90"
          data-testid="new-expense-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Registrar gasto
        </Button>
      </div>

      {/* Date Range */}
      <Card className="border-slate-200 bg-white">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-slate-500">Desde</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40 bg-slate-100 border-slate-200"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-slate-500">Hasta</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40 bg-slate-100 border-slate-200"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 bg-white col-span-1">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-red-500/20">
                <TrendingDown className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total gastos</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  {formatCurrency(totalExpenses)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {Object.entries(expensesByCategory).slice(0, 3).map(([category, amount]) => {
          const Icon = categoryIcons[category] || DollarSign;
          return (
            <Card key={category} className="border-slate-200 bg-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-slate-100">
                    <Icon className="w-5 h-5 text-slate-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{expenseCategoryLabels[category]}</p>
                    <p className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
                      {formatCurrency(amount)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Expenses Table */}
      <Card className="border-slate-200 bg-white">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope' }}>Detalle de gastos</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
            </div>
          ) : expenses.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <DollarSign className="w-12 h-12 mb-3 opacity-50" />
              <p>No hay gastos registrados</p>
            </div>
          ) : (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead>Monto</TableHead>
                  <TableHead>Notas</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((expense, index) => {
                  const Icon = categoryIcons[expense.category] || DollarSign;
                  return (
                    <TableRow 
                      key={expense.id}
                      className="animate-fade-in"
                      style={{ animationDelay: `${index * 0.03}s` }}
                    >
                      <TableCell>
                        <span className="text-slate-500">{formatDate(expense.date)}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-slate-500 border-slate-200">
                          <Icon className="w-3 h-3 mr-1" />
                          {expenseCategoryLabels[expense.category]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{expense.description}</span>
                      </TableCell>
                      <TableCell>
                        <span className="font-bold text-red-400">{formatCurrency(expense.amount)}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-500">{expense.notes || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 hover:bg-slate-100 hover:text-red-400"
                          onClick={() => handleDelete(expense)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* New Expense Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-slate-200 max-h-[90vh] overflow-y-auto scrollbar-visible">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Registrar gasto</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Categoría</Label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger className="bg-slate-100 border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {Object.entries(expenseCategoryLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Descripción *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-slate-100 border-slate-200"
                placeholder="Ej: Pago de renta local"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Monto *</Label>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                  className="bg-slate-100 border-slate-200"
                  min={0}
                  step={0.01}
                />
              </div>
              <div className="space-y-2">
                <Label>Fecha</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="bg-slate-100 border-slate-200"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="bg-slate-100 border-slate-200"
                placeholder="Notas adicionales..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-200">
              Cancelar
            </Button>
            <Button onClick={handleSubmit} className="bg-primary text-white hover:bg-primary/90">
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
